import { Close as CloseIcon } from "@mui/icons-material";
import BusinessIcon from "@mui/icons-material/Business";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  DomainCandidateProfile,
  researchApi,
} from "../../../services/researchApi";
import LogoLoader from "../../common/LogoLoader";
import { getDisplayNameFromDbValue } from "../../../utils/organizationCategoryMapping";

interface DomainCandidateOverlayProps {
  /** The synthetic id, e.g. "domain:linssenyachts.com". */
  kgEntityId: string;
  trackId: string;
  onClose: () => void;
  /** Navigate the overlay to another entity (the conflicting candidate). */
  onEntityClick?: (entityId: string) => void;
  /** Called after this candidate was removed, so the list refreshes. */
  onRemoved?: () => void;
}

const CHIP_LIMIT = 6;

const humanize = (value: string): string => {
  try {
    return getDisplayNameFromDbValue(value);
  } catch {
    const spaced = value.replace(/_/g, " ");
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  }
};

/** Chip list in the graph profile's Business Operations style:
 * first CHIP_LIMIT chips, remainder behind a "+N more" tooltip. */
const ChipGroup: React.FC<{
  label: string;
  values?: string[] | null;
  color: "info" | "success" | "warning" | "default";
}> = ({ label, values, color }) => {
  if (!values || values.length === 0) return null;
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {values.slice(0, CHIP_LIMIT).map((value, index) => (
          <Chip
            key={index}
            label={value}
            color={color}
            variant="outlined"
            size="small"
          />
        ))}
        {values.length > CHIP_LIMIT && (
          <Tooltip
            title={
              <Box>
                {values.slice(CHIP_LIMIT).map((value, idx) => (
                  <div key={idx}>{value}</div>
                ))}
              </Box>
            }
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ ml: 1, alignSelf: "center", cursor: "help" }}
            >
              +{values.length - CHIP_LIMIT} more
            </Typography>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Grid item xs={3}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" sx={{ fontWeight: 500 }} component="div">
      {children || "-"}
    </Typography>
  </Grid>
);

/** Section card in the graph profile's style. */
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <Box
    sx={{
      backgroundColor: "background.paper",
      p: 3,
      borderRadius: 2,
      border: 1,
      borderColor: "divider",
      mb: 1,
    }}
  >
    <Typography variant="h6" gutterBottom sx={{ color: "primary.main", mb: 3 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

/** Full-panel empty-state: large centered icon, headline, message, actions. */
const CenteredState: React.FC<{
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
}> = ({ icon, title, children, actions }) => (
  <Box
    sx={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      px: 6,
      py: 8,
      gap: 1.5,
    }}
  >
    <Box
      sx={{
        width: 96,
        height: 96,
        borderRadius: "50%",
        backgroundColor: "action.hover",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: 1,
        "& .MuiSvgIcon-root": { fontSize: 44, color: "text.secondary" },
      }}
    >
      {icon}
    </Box>
    <Typography variant="h6">{title}</Typography>
    <Box sx={{ maxWidth: 420 }}>{children}</Box>
    {actions && <Box sx={{ display: "flex", gap: 1.5, mt: 2 }}>{actions}</Box>}
  </Box>
);

/** Right-hand detail overlay for candidates not (yet) in the knowledge graph.
 *
 * Mirrors the graph organization profile's layout (functional header, content
 * header, bordered section cards), but renders from the webcontent profile
 * pulled on demand from the domain database: while the crawl is still running
 * we show a "gathering" notice, a terminally failed crawl shows why, and once
 * an extraction exists the snapshot renders here (and is persisted onto the
 * candidate row server-side).
 */
const DomainCandidateOverlay: React.FC<DomainCandidateOverlayProps> = ({
  kgEntityId,
  trackId,
  onClose,
  onEntityClick,
  onRemoved,
}) => {
  const [data, setData] = useState<DomainCandidateProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  const handleRemove = async () => {
    if (!data?.track_entity_id) return;
    setRemoving(true);
    try {
      await researchApi.removeTrackEntity(data.track_entity_id);
      onRemoved?.();
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to remove the candidate");
      setRemoving(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    researchApi
      .getDomainCandidateProfile(trackId, kgEntityId)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((err: any) => {
        if (!cancelled)
          setError(err.message || "Failed to load candidate details");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [trackId, kgEntityId]);

  const domain = data?.domain || kgEntityId.replace(/^domain:/, "");
  const profile = data?.profile;
  const title = data?.name || profile?.organization_name || domain;
  // ISO2 preferred for parity with graph org profiles; older snapshots
  // predate the derived column and fall back to the extracted name.
  const homeCountry =
    profile?.organization_headquarters_country_iso2 ||
    profile?.organization_headquarters_country;
  const headquarters = [profile?.organization_headquarters_city, homeCountry]
    .filter(Boolean)
    .join(", ");

  return (
    <Box
      data-overlay-content
      sx={{
        position: "fixed",
        top: 0,
        right: 0,
        width: "45%",
        height: "100%",
        backgroundColor: "background.paper",
        borderLeft: 1,
        borderColor: "divider",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
        {/* Functional header (matches EntityFunctionalHeader) */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
            position: "sticky",
            top: 0,
            zIndex: 10,
            backgroundColor: "background.paper",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
            <BusinessIcon
              sx={{ fontSize: 20, mr: 1, color: "primary.main" }}
            />
            <Typography variant="h6">Organization Details</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content header (matches EntityContentHeader) */}
        <Box sx={{ pt: 3, pb: 0, pl: 5, pr: 4, flexShrink: 0 }}>
          <Typography variant="h4" gutterBottom>
            {title}
          </Typography>
          <Box sx={{ mb: 3 }}>
            {profile?.organization_type && (
              <Chip
                label={humanize(profile.organization_type)}
                variant="filled"
                sx={{ mr: 1, mb: 0.5 }}
              />
            )}
            {!data?.conflict && (
              <Tooltip title="This candidate was added by domain and is queued for knowledge-graph ingestion; it will be linked automatically once available.">
                <Chip
                  label="Pending graph ingestion"
                  color="info"
                  variant="outlined"
                  sx={{ mr: 1, mb: 0.5 }}
                />
              </Tooltip>
            )}
          </Box>
          {profile?.organization_description && (
            <Typography
              variant="body1"
              color="text.secondary"
              paragraph
              marginBottom={0}
            >
              {profile.organization_description}
            </Typography>
          )}
        </Box>

        {/* Body — non-ready states render as a full-panel centered
            composition (icon, message, actions); a duplicate takes
            precedence over everything since removal is the only next step */}
        {loading ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LogoLoader size="section" surface="paper" label="Loading" />
          </Box>
        ) : data?.conflict ? (
          <CenteredState
            icon={<ContentCopyIcon />}
            title="Duplicate entry"
            actions={
              <>
                {onEntityClick && (
                  <Button
                    variant="contained"
                    onClick={() => onEntityClick(data.conflict!.kg_entity_id)}
                  >
                    View existing candidate
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  disabled={removing}
                  onClick={handleRemove}
                >
                  Remove from list
                </Button>
              </>
            }
          >
            <Typography variant="body2" color="text.secondary">
              {data.conflict.redirected_to ? (
                <>
                  <strong>{domain}</strong> redirects to{" "}
                  <strong>{data.conflict.redirected_to}</strong>, which is
                  already in this list as{" "}
                  <strong>{data.conflict.name || "an existing candidate"}</strong>
                  .
                </>
              ) : (
                <>
                  This organization is already in this list as{" "}
                  <strong>{data.conflict.name || "an existing candidate"}</strong>
                  .
                </>
              )}
            </Typography>
            {data.conflict.has_history && (
              <Typography variant="caption" color="text.secondary">
                Note: this entry has discovery or evaluation results that will
                be discarded on removal.
              </Typography>
            )}
          </CenteredState>
        ) : error ? (
          <CenteredState icon={<ErrorOutlineIcon />} title="Couldn't load candidate">
            <Typography variant="body2" color="text.secondary">
              {error}
            </Typography>
          </CenteredState>
        ) : data?.profile_status === "gathering" ? (
          <CenteredState icon={<TravelExploreIcon />} title="Gathering website data">
            <Typography variant="body2" color="text.secondary">
              We're currently collecting information from{" "}
              <strong>{domain}</strong>. This usually takes a few minutes —
              check back shortly.
            </Typography>
          </CenteredState>
        ) : data?.profile_status === "failed" ? (
          <CenteredState icon={<CloudOffIcon />} title="No website data available">
            <Typography variant="body2" color="text.secondary">
              We couldn't gather data from <strong>{domain}</strong>. The
              website may be unreachable, or it may be blocking automated
              access.
            </Typography>
            {data.error && (
              <Typography variant="caption" color="text.secondary">
                Details: {data.error}
              </Typography>
            )}
          </CenteredState>
        ) : profile ? (
          <Box sx={{ p: 2, flexShrink: 0 }}>
            <Grid container spacing={3}>
              {/* Company Overview (matches OrganizationDetail) */}
              <Grid item xs={12}>
                <Section title="Company Overview">
                  <Grid container spacing={2}>
                    <Fact label="Home Country">{homeCountry}</Fact>
                    <Fact label="Websites">
                      <a
                        href={"https://" + domain}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "inherit",
                          textDecoration: "underline",
                        }}
                      >
                        {domain}
                      </a>
                    </Fact>
                    <Fact label="Founding Year">
                      {profile.organization_founding_year}
                    </Fact>
                    <Fact label="Employees">
                      {profile.organization_number_of_employees}
                    </Fact>
                    <Fact label="Headquarters">{headquarters}</Fact>
                    <Fact label="Data Sources">
                      <Tooltip title="Extracted from the company website">
                        <span>1</span>
                      </Tooltip>
                    </Fact>
                  </Grid>
                  {profile.organization_ownership && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Ownership
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {profile.organization_ownership}
                      </Typography>
                    </Box>
                  )}
                </Section>
              </Grid>

              {/* Business Operations (matches OrganizationDetail) */}
              {((profile.services?.length || 0) > 0 ||
                (profile.products?.length || 0) > 0 ||
                (profile.target_markets?.length || 0) > 0 ||
                (profile.certifications?.length || 0) > 0) && (
                <Grid item xs={12}>
                  <Section title="Business Operations">
                    <ChipGroup
                      label="Services Offered:"
                      values={profile.services}
                      color="info"
                    />
                    <ChipGroup
                      label="Products Produced:"
                      values={profile.products}
                      color="success"
                    />
                    <ChipGroup
                      label="Target Markets:"
                      values={profile.target_markets}
                      color="warning"
                    />
                    <ChipGroup
                      label="Certifications:"
                      values={profile.certifications}
                      color="default"
                    />
                  </Section>
                </Grid>
              )}
            </Grid>
          </Box>
        ) : (
          <CenteredState icon={<TravelExploreIcon />} title="No website data yet">
            <Typography variant="body2" color="text.secondary">
              No website data is available for this candidate yet.
            </Typography>
          </CenteredState>
        )}
      </Box>
    </Box>
  );
};

export default DomainCandidateOverlay;
